import { useAuth } from '@clerk/clerk-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { FullPageCta } from '@/shared/components/FullPageCta';
import { useOnboarding } from '@/shared/hooks/useInteractions';

const DEFAULT_REDIRECT_PATH = '/deal-intelligence';

export function GetStartedPage() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { getToken, sessionClaims } = useAuth();
  const { redirect } = useSearch({ from: '/_onboarding/get-started' });

  const redirectPath = redirect ?? DEFAULT_REDIRECT_PATH;
  const alreadyOnboarded = Boolean(sessionClaims?.metadata.companyId);

  const goToRequestedPage = async () => {
    await getToken({ skipCache: true });
    navigate({ to: redirectPath, replace: true });
  };

  const handleStartError = (error: Error) => {
    toast.error(t('getStarted.failed'), {
      description: error.message,
    });
  };

  const { mutate: completeOnboarding, isPending: isStarting } = useOnboarding({
    onSuccess: goToRequestedPage,
    onError: handleStartError,
  });

  const handleStart = () => {
    if (alreadyOnboarded) {
      void goToRequestedPage();
      return;
    }
    completeOnboarding({});
  };

  return (
    <div className="min-h-screen w-full bg-page-bg bg-[radial-gradient(60%_50%_at_50%_0%,rgba(34,209,197,0.12),transparent_70%),radial-gradient(40%_40%_at_85%_85%,rgba(22,120,194,0.10),transparent_75%)]">
      <div className="mx-auto flex min-h-screen max-w-5xl flex-col px-6 py-6 sm:px-10 sm:py-10">
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <img
            src="/intera_logo_blue.svg"
            alt="Intera"
            className="h-12 w-auto"
          />

          <h1 className="mt-7 text-balance text-5xl font-bold leading-[1.02] tracking-tight text-foreground sm:text-6xl">
            {t('getStarted.title')}
          </h1>

          <FullPageCta
            onClick={handleStart}
            disabled={isStarting}
            className="mt-10"
          >
            {isStarting ? (
              <>
                {t('getStarted.starting')}
                <Loader2 className="h-4 w-4 animate-spin" />
              </>
            ) : (
              <>
                {t('getStarted.cta')}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </>
            )}
          </FullPageCta>
        </div>
      </div>
    </div>
  );
}
